<template>
  <header class="app-header">
    <div class="container">
      <div class="header-content">
        <!-- Logo 和店铺名称 -->
        <div class="logo-section" @click="$router.push('/')">
          <div class="logo-icon">
            <el-icon><Shop /></el-icon>
          </div>
          <div class="shop-info">
            <h1 class="shop-name">{{ currentShopName }}</h1>
            <p class="shop-slogan">{{ $t('shop.slogan') }}</p>
          </div>
        </div>

        <!-- 导航菜单 -->
        <nav class="main-nav">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{
              active: $route.path === item.path,
              loading: isNavigating && pendingRoute === item.path
            }"
            @click="handleNavClick(item.path, $event)"
          >
            <el-icon>
              <Loading v-if="isNavigating && pendingRoute === item.path" />
              <component v-else :is="item.icon" />
            </el-icon>
            <span>{{ $t(item.label) }}</span>
          </router-link>
        </nav>

        <!-- 右侧工具栏 -->
        <div class="toolbar">
          <!-- 搜索框 -->
          <div class="search-box">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('common.search')"
              @keyup.enter="handleSearch"
              clearable
            >
              <template #suffix>
                <el-icon class="search-icon" @click="handleSearch">
                  <Search />
                </el-icon>
              </template>
            </el-input>
          </div>

          <!-- 移动端搜索按钮 -->
          <div class="mobile-search-btn" @click="handleSearch">
            <el-icon><Search /></el-icon>
          </div>

          <!-- 语言切换 -->
          <el-dropdown @command="handleLanguageChange" class="language-dropdown">
            <span class="dropdown-link">
              <span class="flag">{{ currentLanguageFlag }}</span>
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="lang in languages"
                  :key="lang.key"
                  :command="lang.key"
                  :class="{ active: currentLanguage === lang.key }"
                >
                  <span class="flag">{{ lang.flag }}</span>
                  {{ lang.name }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <!-- 主题切换 -->
          <el-dropdown @command="handleThemeChange" class="theme-dropdown">
            <span class="dropdown-link">
              <el-icon><Sunny /></el-icon>
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="theme in themes"
                  :key="theme.key"
                  :command="theme.key"
                  :class="{ active: currentTheme === theme.key }"
                >
                  {{ currentLanguage === 'zh-CN' ? theme.name : theme.nameEn }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 移动端菜单按钮 -->
    <div class="mobile-menu-btn" @click="toggleMobileMenu">
      <el-icon><Menu /></el-icon>
    </div>

    <!-- 移动端菜单 -->
    <transition name="slide-down">
      <div v-if="showMobileMenu" class="mobile-menu">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="mobile-nav-item"
          :class="{
            active: $route.path === item.path,
            loading: isNavigating && pendingRoute === item.path
          }"
          @click="handleMobileNavClick(item.path, $event)"
        >
          <el-icon>
            <Loading v-if="isNavigating && pendingRoute === item.path" />
            <component v-else :is="item.icon" />
          </el-icon>
          <span>{{ $t(item.label) }}</span>
        </router-link>
      </div>
    </transition>
  </header>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  Shop,
  House,
  Grid,
	Medal,
  Star,
  InfoFilled,
  Search,
  Menu,
  ArrowDown,
  Sunny,
  Loading
} from '@element-plus/icons-vue'

export default {
  name: 'Header',
  components: {
    Shop,
    House,
    Grid,
		Medal,
    Star,
    InfoFilled,
    Search,
    Menu,
    ArrowDown,
    Sunny,
    Loading
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const { t, locale } = useI18n()

    const searchKeyword = ref('')
    const showMobileMenu = ref(false)
    const isNavigating = ref(false)
    const pendingRoute = ref('')

    // 导航菜单项
    const navItems = [
      { path: '/', label: 'nav.home', icon: House },
      { path: '/categories', label: 'nav.categories', icon: Grid },
      { path: '/hot', label: 'nav.hot', icon: Medal },
      { path: '/new', label: 'nav.new', icon: Star },
      { path: '/about', label: 'nav.about', icon: InfoFilled }
    ]

    // 计算属性
    const currentLanguage = computed(() => store.getters['language/currentLanguage'])
    const languages = computed(() => store.getters['language/languages'])
    const currentTheme = computed(() => store.getters['theme/currentTheme'])
    const themes = computed(() => store.getters['theme/themes'])
    const shopName = computed(() => store.getters['shop/shopName'])
    const shopNameEn = computed(() => store.getters['shop/shopNameEn'])

    const currentLanguageFlag = computed(() => {
      const lang = languages.value.find(l => l.key === currentLanguage.value)
      return lang ? lang.flag : '🇨🇳'
    })

    const currentShopName = computed(() => {
      return currentLanguage.value === 'zh-CN' ? shopName.value : shopNameEn.value
    })

    // 方法
    const handleSearch = () => {
      const keyword = searchKeyword.value.trim()
      if (keyword) {
        // 如果已经在搜索页面且关键词相同，不需要重复跳转
        if (route.name === 'Search' && route.query.q === keyword) {
          return
        }

        router.push({
          name: 'Search',
          query: { q: keyword }
        })

        // 清空搜索框（可选）
        // searchKeyword.value = ''
      }
    }

    const handleLanguageChange = (language) => {
      store.dispatch('language/setLanguage', language)
      locale.value = language
    }

    const handleThemeChange = (theme) => {
      store.dispatch('theme/setTheme', theme)
    }

    const toggleMobileMenu = () => {
      showMobileMenu.value = !showMobileMenu.value
    }

    const handleNavClick = (path, event) => {
      if (route.path !== path) {
        // 阻止默认行为，避免router-link的即时跳转
        event.preventDefault()

        isNavigating.value = true
        pendingRoute.value = path

        // 延迟导航，确保UI状态已更新
        setTimeout(() => {
          router.push(path)
        }, 50)

        // 设置超时，确保状态不会永远卡住
        setTimeout(() => {
          isNavigating.value = false
          pendingRoute.value = ''
        }, 1200)
      }
    }

    const handleMobileNavClick = (path, event) => {
      if (route.path !== path) {
        event.preventDefault()
        toggleMobileMenu() // 先关闭菜单

        isNavigating.value = true
        pendingRoute.value = path

        setTimeout(() => {
          router.push(path)
        }, 100)

        setTimeout(() => {
          isNavigating.value = false
          pendingRoute.value = ''
        }, 1200)
      } else {
        toggleMobileMenu() // 如果是当前页面，只关闭菜单
      }
    }

    // 监听路由变化，重置导航状态
    router.afterEach(() => {
      setTimeout(() => {
        isNavigating.value = false
        pendingRoute.value = ''
      }, 100)
    })

    return {
      searchKeyword,
      showMobileMenu,
      navItems,
      currentLanguage,
      languages,
      currentTheme,
      themes,
      currentLanguageFlag,
      currentShopName,
      handleSearch,
      handleLanguageChange,
      handleThemeChange,
      toggleMobileMenu,
      handleNavClick,
      handleMobileNavClick,
      isNavigating,
      pendingRoute
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  background-color: var(--header-bg);
  border-bottom: 1px solid var(--border-color);
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 1000;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;

    @media (max-width: 768px) {
      height: 50px;
    }
  }

  .logo-section {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: 480px) {
      // 在超小屏幕上减少间距
      margin-right: 8px;
    }

    &:hover {
      transform: scale(1.05);
    }

    .logo-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;

      @media (max-width: 768px) {
        width: 36px;
        height: 36px;
        margin-right: 8px;
      }

      @media (max-width: 480px) {
        width: 32px;
        height: 32px;
        margin-right: 6px;
      }

      .el-icon {
        font-size: 20px;
        color: white;

        @media (max-width: 768px) {
          font-size: 18px;
        }

        @media (max-width: 480px) {
          font-size: 16px;
        }
      }
    }

    .shop-info {
      .shop-name {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
        color: var(--text-color);
        line-height: 1.2;

        @media (max-width: 768px) {
          font-size: 16px;
        }
      }

      .shop-slogan {
        font-size: 12px;
        color: var(--text-color);
        opacity: 0.7;
        margin: 0;
        line-height: 1;

        @media (max-width: 768px) {
          display: none;
        }
      }
    }
  }

  .main-nav {
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 768px) {
      display: none;
    }

    .nav-item {
      display: flex;
      align-items: center;
      padding: 8px 16px;
      border-radius: 6px;
      color: var(--text-color);
      text-decoration: none;
      transition: all 0.3s ease;
      position: relative;

      .el-icon {
        margin-right: 6px;
        font-size: 16px;
      }

      &:hover {
        background-color: var(--primary-color);
        color: white;
        transform: translateY(-2px);
      }

      &.active {
        background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
        color: white;

        &::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 50%;
          transform: translateX(-50%);
          width: 6px;
          height: 6px;
          background-color: white;
          border-radius: 50%;
        }
      }

      &.loading {
        background-color: var(--primary-color);
        color: white;
        pointer-events: none;

        .el-icon {
          animation: spin 1s linear infinite;
        }
      }
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 8px;
    }

    @media (max-width: 480px) {
      gap: 6px;
    }

    .search-box {
      width: 240px;

      @media (max-width: 768px) {
        width: 160px;
      }

      @media (max-width: 480px) {
        width: 100px;
      }

      @media (max-width: 360px) {
        display: none; // 在超小屏幕上隐藏搜索框
      }

      .search-icon {
        cursor: pointer;
        transition: color 0.3s ease;

        &:hover {
          color: var(--primary-color);
        }
      }
    }

    .mobile-search-btn {
      display: none;

      @media (max-width: 360px) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s ease;
        color: var(--text-color);

        &:hover {
          background-color: var(--primary-color);
          color: white;
        }

        .el-icon {
          font-size: 16px;
        }
      }
    }

    .language-dropdown,
    .theme-dropdown {
      .dropdown-link {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px 12px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s ease;
        color: var(--text-color);

        @media (max-width: 768px) {
          padding: 4px 8px;
          gap: 2px;
        }

        @media (max-width: 480px) {
          padding: 4px 6px;

          // 在超小屏幕上隐藏文字，只显示图标
          span:not(.flag) {
            display: none;
          }
        }

        &:hover {
          background-color: var(--primary-color);
          color: white;
        }

        .flag {
          font-size: 16px;

          @media (max-width: 480px) {
            font-size: 14px;
          }
        }
      }
    }
  }

  .mobile-menu-btn {
    display: none;

    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background-color: var(--primary-color);
        color: white;
      }
    }
  }

  .mobile-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--header-bg);
    border-bottom: 1px solid var(--border-color);
    box-shadow: var(--shadow);
    z-index: 1000;
    max-height: 80vh;
    overflow-y: auto;

    .mobile-nav-item {
      display: flex;
      align-items: center;
      padding: 14px 20px;
      color: var(--text-color);
      text-decoration: none;
      border-bottom: 1px solid var(--border-color);
      transition: all 0.3s ease;
      font-size: 16px;
      font-weight: 500;

      @media (max-width: 480px) {
        padding: 12px 16px;
        font-size: 15px;
      }

      .el-icon {
        margin-right: 12px;
        font-size: 18px;

        @media (max-width: 480px) {
          margin-right: 10px;
          font-size: 16px;
        }
      }

      &:hover,
      &.active {
        background-color: var(--primary-color);
        color: white;
        transform: translateX(4px);
      }

      &:last-child {
        border-bottom: none;
      }

      &.loading {
        background-color: var(--primary-color);
        color: white;
        pointer-events: none;

        .el-icon {
          animation: spin 1s linear infinite;
        }
      }
    }
  }
}

// 下拉菜单激活状态
.el-dropdown-menu__item.active {
  background-color: var(--primary-color);
  color: white;
}

// 动画
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

// 旋转动画
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
